import { useCallback, useEffect, useState } from 'react';

type UseTokenExpirationOptions = {
  enabled?: boolean;
  onExpired?: () => void;
};

type UseTokenExpirationReturn = {
  idpToken: string | null;
  tokenExpiresAt: number | null;
  setToken: (token: string, expiresAt: number) => void;
  clearToken: () => void;
};

export const useTokenExpiration = ({
  enabled = false,
  onExpired,
}: UseTokenExpirationOptions): UseTokenExpirationReturn => {
  const [idpToken, setIdpToken] = useState<string | null>(null);
  const [tokenExpiresAt, setTokenExpiresAt] = useState<number | null>(null);

  const setToken = useCallback((token: string, expiresAt: number) => {
    setIdpToken(token);
    setTokenExpiresAt(expiresAt);
  }, []);

  const clearToken = useCallback(() => {
    setIdpToken(null);
    setTokenExpiresAt(null);
  }, []);

  useEffect(() => {
    if (!enabled || !tokenExpiresAt) {
      return;
    }

    const updateTimer = () => {
      const remaining = Math.max(0, tokenExpiresAt - Date.now());

      if (remaining === 0) {
        clearToken();
        onExpired?.();
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [enabled, tokenExpiresAt, onExpired, clearToken]);

  return {
    idpToken,
    tokenExpiresAt,
    setToken,
    clearToken,
  };
};
